import capitalize from 'lodash/capitalize';
import isEmpty from 'lodash/isEmpty';
import negate from 'lodash/negate';
import startCase from 'lodash/startCase';
import differenceBy from 'lodash/differenceBy';
import unionBy from 'lodash/unionBy';
import isNil from 'lodash/isNil';
import { getBillEstimateV2 } from '@atlassian/temp-proxies';
import { isStatuspage, isJCAProduct, isJSMProduct } from '@atlassian/cssxp-utils-product';
import { LinkedEntitlementsInstance } from '@atlassian/cssxp-typescommon';
import { OrderStatus, Promotion, Benefit, SubBenefitType } from '@css-xp/graphtypes';
import {
  OrderItemRequestType,
  OrderLineItem,
  SubscriptionScheduleAction,
  LinkedProductTypes,
  EntitlementDetails,
  RelationshipPathType,
  LinkedDestinationEntitlement,
  LinkedEntitlementTypes,
  Order,
  BillingStatus,
  ProvisioningStatus,
  TransactionAccount,
  Maybe,
} from '@css-xp/graphtypes/src/generated-types-new';
import {
  isCollectionInstanceEntitlement,
  isCollectionProductEntitlement,
  isProductBelongsToJiraFamily,
  isMultiInstanceAppInstance,
} from '@css-xp/ppc-utils';

export interface PaymentMethodProps {
  invoiceGroupId?: Maybe<string> | undefined;
  transactionAccount: Maybe<TransactionAccount>;
  paymentIssueStatusEnabled?: any;
}

type OmsPayload = Parameters<typeof getBillEstimateV2>[0];
// direct using CustomOrderEntitlement from package has version related issues
export type CustomOrderEntitlement = NonNullable<OmsPayload['entitlements']>[0];

export const getDescriptor = (item: Maybe<OrderLineItem>, baseProduct?: string, billToCompanyName?: string) => {
  const pricingPlanName = item?.pricingPlan?.type;
  const pricingPlanFrequency = item?.pricingPlan?.billingFrequency;
  let offeringName;
  if (isNil(item?.skipTrial) || item?.skipTrial) {
    offeringName = item?.offering?.name;
  } else if (item?.offering?.name) {
    offeringName = item?.offering?.name + ' Trial';
  }

  if (item?.type === OrderItemRequestType.REVERT_TRIAL_ORDER) {
    offeringName = `${baseProduct} ${item?.isApp ? ` Advanced` : ` Premium`}`;
  }

  if (
    item?.type === OrderItemRequestType.CANCELLATION_ORDER ||
    item?.type === OrderItemRequestType.SYSTEM_CANCELLATION_ORDER
  ) {
    if (item.status === OrderStatus.SUCCESS) {
      return 'Subscription Cancelled';
    } else if (item.status === OrderStatus.FAIL) {
      return;
    }
  }

  if (item?.type === OrderItemRequestType.TRANSFER_OUT_ORDER) {
    if (item.status === OrderStatus.SUCCESS) {
      return 'success';
    } else if (item.status === OrderStatus.FAIL) {
      return;
    }
  }

  // return empty string if it is for patch entitlement
  if (item?.type === OrderItemRequestType.PATCH_ENTITLEMENT_DETAILS_ORDER) {
    return '';
  }

  // return empty string if it is for transition order
  if (item?.type === OrderItemRequestType.TRANSITION_ORDER) {
    return '';
  }

  const values = [offeringName, pricingPlanName, pricingPlanFrequency]
    .filter(negate(isEmpty))
    //@ts-ignore
    .map(capitalize)
    .map(startCase);

  if (values.length > 0) {
    return ` ${values.join(' - ')}`;
  }

  if (billToCompanyName && item?.type === OrderItemRequestType.ACCOUNT_MODIFICATION_ORDER) {
    return ` ${billToCompanyName}`;
  }

  return 'unknown';
};

export const getId = (item: Maybe<OrderLineItem>) => {
  return ` (${item?.entitlementNumber ?? item?.entitlementId})`;
};

export const getOrderLineItemStatusAndType = (
  status?: Maybe<OrderStatus>,
  type?: Maybe<OrderItemRequestType>,
  promotions?: OrderLineItem['promotions'],
  accountModification?: OrderLineItem['orderLineItemsAccountModification'],
  withDescription = false
  // eslint-disable-next-line max-params
) => {
  let statusMessage;
  if (status === OrderStatus.FAIL) {
    statusMessage = withDescription ? 'Fail' : 'Failed to';
  }

  let typeMessage;
  switch (type) {
    case OrderItemRequestType.AMENDMENT_ORDER_OF_FAMILY:
      typeMessage = statusMessage ? 'amend' : 'Amending of family';
      break;
    case OrderItemRequestType.ACCOUNT_MODIFICATION_ORDER_OF_FAMILY:
      typeMessage = statusMessage ? 'update' : 'Update of family';
      break;
    case OrderItemRequestType.TRANSFER_IN_ORDER_OF_FAMILY:
      typeMessage = statusMessage ? 'transfer' : 'Transfer of family';
      break;
    case OrderItemRequestType.TRANSFER_OUT_ORDER_OF_FAMILY:
      typeMessage = statusMessage ? 'transfer out' : 'Transfer out of family';
      break;
    case OrderItemRequestType.CREATION_ORDER_OF_FAMILY:
      typeMessage = statusMessage ? 'create' : 'Creation of family';
      break;
    case OrderItemRequestType.CANCELLATION_ORDER_OF_FAMILY:
      typeMessage = statusMessage ? 'cancel' : 'Cancellation of family';
      break;
    case OrderItemRequestType.CREATION_ORDER:
      typeMessage = statusMessage ? 'create' : 'Created';
      break;
    case OrderItemRequestType.TRANSFER_IN_ORDER:
      typeMessage = withDescription ? 'transfer' : 'Transfer';
      break;
    case OrderItemRequestType.TRANSFER_OUT_ORDER:
      typeMessage = withDescription ? 'transfer out' : 'Transfer out';
      break;
    case OrderItemRequestType.TRANSITION_ORDER:
      typeMessage = withDescription ? 'transition' : 'Transition';
      break;
    case OrderItemRequestType.AMENDMENT_ORDER:
    case OrderItemRequestType.SYSTEM_AMENDMENT_ORDER: {
      const hasTrialPromotion = promotions?.some(
        //@ts-ignore
        (promotion: Promotion) => {
          return promotion?.benefits?.some(
            //@ts-ignore
            (benefit: Benefit) => {
              return benefit.subBenefitType === SubBenefitType.TRIAL;
            }
          );
        }
      );

      typeMessage = statusMessage
        ? 'amend'
        : hasTrialPromotion
        ? 'Extended'
        : withDescription
        ? 'Amended'
        : 'Amended to';
      break;
    }
    case OrderItemRequestType.CANCELLATION_ORDER:
    case OrderItemRequestType.SYSTEM_CANCELLATION_ORDER:
      typeMessage = statusMessage ? 'cancel' : withDescription ? 'Cancel' : 'Cancelled';
      break;
    case OrderItemRequestType.REACTIVATION_ORDER:
      typeMessage = statusMessage ? 'reactivate' : withDescription ? 'Reactivate' : 'Reactivated';
      break;
    case OrderItemRequestType.ACCOUNT_MODIFICATION_ORDER:
      if (accountModification?.accountModificationTransactionAccountId) {
        typeMessage = statusMessage ? 'change billing account' : withDescription ? 'Update' : 'Changed billing account';
      } else {
        typeMessage = statusMessage ? 'update billing profile' : withDescription ? 'Update' : 'Updated billing profile';
      }
      break;
    case OrderItemRequestType.REWIND_ORDER:
      typeMessage = statusMessage ? 'rewind' : withDescription ? 'Rewind' : 'Rewind -';
      break;
    case OrderItemRequestType.REBILL_ORDER:
      typeMessage = statusMessage ? 'rebill' : withDescription ? 'Rebill' : 'Rebill -';
      break;
    case OrderItemRequestType.REVERT_TRIAL_ORDER:
      if (withDescription) {
        typeMessage = 'opted out';
      } else {
        typeMessage = statusMessage ? 'opt out' : 'Opted out from';
      }
      break;
    case OrderItemRequestType.PATCH_ENTITLEMENT_DETAILS_ORDER: {
      if (withDescription) {
        typeMessage = 'amended';
      } else {
        typeMessage = statusMessage ? 'patched' : 'Patched';
      }
      break;
    }
    default:
      break;
  }

  if (statusMessage) {
    return `${statusMessage} ${typeMessage}`;
  }
  return `${typeMessage}`;
};

export const getOrderType = (status?: Maybe<OrderStatus>, type?: Maybe<OrderItemRequestType>) => {
  let isPurchaseOrder = false;
  let statusMessage;
  if (status === OrderStatus.FAIL) {
    statusMessage = ' failed';
  }

  let typeMessage;
  switch (type) {
    case OrderItemRequestType.AMENDMENT_ORDER_OF_FAMILY:
    case OrderItemRequestType.ACCOUNT_MODIFICATION_ORDER_OF_FAMILY:
    case OrderItemRequestType.TRANSITION_ORDER:
    case OrderItemRequestType.AMENDMENT_ORDER:
    case OrderItemRequestType.SYSTEM_AMENDMENT_ORDER:
    case OrderItemRequestType.ACCOUNT_MODIFICATION_ORDER:
    case OrderItemRequestType.REVERT_TRIAL_ORDER:
    case OrderItemRequestType.PATCH_ENTITLEMENT_DETAILS_ORDER:
    case OrderItemRequestType.REWIND_ORDER:
    case OrderItemRequestType.REBILL_ORDER:
      typeMessage = 'change';
      break;
    case OrderItemRequestType.TRANSFER_IN_ORDER_OF_FAMILY:
    case OrderItemRequestType.TRANSFER_OUT_ORDER_OF_FAMILY:
    case OrderItemRequestType.TRANSFER_IN_ORDER:
    case OrderItemRequestType.TRANSFER_OUT_ORDER:
      typeMessage = 'transfer';
      break;
    case OrderItemRequestType.CREATION_ORDER_OF_FAMILY:
    case OrderItemRequestType.CREATION_ORDER:
      typeMessage = 'purchase';
      isPurchaseOrder = true;
      break;
    case OrderItemRequestType.CANCELLATION_ORDER_OF_FAMILY:
    case OrderItemRequestType.CANCELLATION_ORDER:
    case OrderItemRequestType.SYSTEM_CANCELLATION_ORDER:
      typeMessage = 'cancellation';
      break;
    case OrderItemRequestType.REACTIVATION_ORDER:
      typeMessage = 'reactivation';
      break;
    default:
      break;
  }

  if (statusMessage) {
    return {
      text: `${typeMessage}${statusMessage}`,
      isPurchaseOrder,
    };
  }

  return {
    text: `${typeMessage}`,
    isPurchaseOrder,
  };
};

export const getAllRemovedPromotions = (
  promotions: Promotion[] | undefined,
  scheduledPromotions: Promotion[] | undefined
) => {
  const idText = 'id';
  // The promotion scheduled to be removed does not show up in the `entitlement.scheduledChanges.promotions`.
  // Hence, get the `removedPromotionsDiff` to handle if there are any scheduled removal of promotion.
  const removedPromotionsDiff = differenceBy(promotions, scheduledPromotions ?? [], idText);

  const inactiveScheduledPromotions = scheduledPromotions?.filter((promotion) => promotion.status !== 'ACTIVE');
  const allRemovedPromotions = unionBy(removedPromotionsDiff, inactiveScheduledPromotions, idText) as Promotion[];

  return allRemovedPromotions;
};

export const getScheduleChangesDescription = (changeAction: SubscriptionScheduleAction | undefined | null) => {
  switch (changeAction) {
    case SubscriptionScheduleAction.CANCEL:
      return 'Cancel';
    case SubscriptionScheduleAction.TRANSFER:
      return 'Transfer';
    case SubscriptionScheduleAction.UPDATE_TRANSFER:
      return 'Update and Transfer';
    case SubscriptionScheduleAction.UPDATE:
    case SubscriptionScheduleAction.UPDATE_CANCEL:
      return 'Update';
  }
};

export const groupOrderLineItems = (order: Maybe<Order>) => {
  return order?.orderLineItems?.nodes?.reduce((acc: any, lineItem: Maybe<OrderLineItem>) => {
    let processFlag: boolean;

    switch (lineItem?.type) {
      case OrderItemRequestType.CANCELLATION_ORDER:
      case OrderItemRequestType.CREATION_ORDER:
      case OrderItemRequestType.ACCOUNT_MODIFICATION_ORDER:
      case OrderItemRequestType.AMENDMENT_ORDER:
      case OrderItemRequestType.TRANSFER_IN_ORDER:
      case OrderItemRequestType.TRANSFER_OUT_ORDER:
        processFlag = true;
        break;
      default:
        processFlag = false;
        break;
    }

    if (!processFlag) {
      acc.push({
        ...lineItem,
        isDisplay: true,
      });
    } else {
      const index = acc.findIndex((element: Maybe<OrderLineItem>) => element?.type === lineItem?.type);

      if (index !== -1) {
        const orderTransitionType = lineItem?.orderTransitionType;
        const salesTransitionType = lineItem?.salesTransitionType;
        const status = lineItem?.status;
        const type = lineItem?.type;
        // const promotions = lineItem.promotions;
        // const orderLineItemsAccountModification = lineItem.orderLineItemsAccountModification;
        if (
          orderTransitionType === acc[index]?.orderTransitionType &&
          salesTransitionType === acc[index]?.salesTransitionType &&
          status === acc[index]?.status &&
          type === acc[index]?.type
          // promotions === acc[index].promotions
          // orderLineItemsAccountModification === acc[index].orderLineItemsAccountModification
        ) {
          acc[index].isDisplay = false;
          const familyIndex = acc.findIndex((element: Maybe<OrderLineItem>) => element?.type === `${type}_OF_FAMILY`);

          if (familyIndex === -1) {
            acc.push(
              ...[
                {
                  status,
                  salesTransitionType,
                  orderTransitionType,
                  type: `${type}_OF_FAMILY`,
                  isDisplay: true,
                  entitlementId: '',
                  lineItemId: '',
                },
                {
                  ...lineItem,
                  isDisplay: false,
                },
              ]
            );
          } else {
            acc.push({
              ...lineItem,
              isDisplay: false,
            });
          }
        } else {
          acc.push({
            ...lineItem,
            isDisplay: true,
          });
        }
      } else {
        acc.push({
          ...lineItem,
          isDisplay: true,
        });
      }
    }

    return acc;
  }, []);
};

export const getBilledForColumnData = (
  productId: string | undefined | null,
  edition: string | undefined | null,
  returnSingular?: boolean
) => {
  const suffix = returnSingular ? '' : 's';
  if (isStatuspage(productId)) {
    if (edition === 'Audience-Specific') {
      return `tier${suffix}`;
    } else {
      return `unit${suffix}`;
    }
  } else {
    return `user${suffix}`;
  }
};

/**
 * Only for JIRA product family we need parent container ID . For confluence & statusPage we are referring entitlement ID .
 */
export const getParentContainerId = (
  entitlement: EntitlementDetails,
  baseProduct?: LinkedProductTypes,
  isJPDAsJiraFamilyEnabled?: boolean
): string | undefined => {
  const productType = entitlement?.productType ?? baseProduct;
  if (productType === LinkedProductTypes.STATUSPAGE) {
    return entitlement?.parentSen ?? entitlement.id;
  }
  const isJiraProduct =
    productType === LinkedProductTypes.JIRA ||
    isProductBelongsToJiraFamily(entitlement?.productId, { isJPDAsJiraFamilyEnabled });
  return isJiraProduct
    ? entitlement?.relatesFromEntitlements
      ? (entitlement?.relatesFromEntitlements.filter(
          (item) =>
            item?.relationshipType ===
            (entitlement?.isApp ? RelationshipPathType.APP_DEPENDENCE : RelationshipPathType.FAMILY_CONTAINER)
        )[0]?.entitlementId as string)
      : undefined
    : undefined;
};

interface LinkedDestinationEntitlementWithDetails extends LinkedDestinationEntitlement {
  subscriptionId?: LinkedEntitlementsInstance['subscriptionId'];
  pricingPlanType?: LinkedEntitlementsInstance['pricingPlanType'];
  skipTrial?: LinkedEntitlementsInstance['skipTrial'];
  isExpiredPricingPlan?: LinkedEntitlementsInstance['isExpiredPricingPlan'];
  promotionIds?: LinkedEntitlementsInstance['promotionIds'];
  customPromotions?: LinkedEntitlementsInstance['customPromotions'];
  addPromotions?: LinkedEntitlementsInstance['addPromotions'];
  promotionsToRemove?: LinkedEntitlementsInstance['promotionsToRemove'];
  operations?: LinkedEntitlementsInstance['operations'];
  chargeType?: LinkedEntitlementsInstance['chargeType'];
  removePromotions?: LinkedEntitlementsInstance['removePromotions'];
  isUpgradeTrialOrder?: LinkedEntitlementsInstance['isUpgradeTrialOrder'];
  isImmediateAmendmentToFree?: LinkedEntitlementsInstance['isImmediateAmendmentToFree'];
}

export const getLinkedEntitlementsDetails = (
  entitlement: EntitlementDetails,
  linkedEntitlementsData: LinkedDestinationEntitlementWithDetails[],
  offeringId?: string
): LinkedEntitlementsInstance[] => {
  const actualEntitlementDetail: LinkedEntitlementsInstance = {
    entitlementId: entitlement.id,
    productId: entitlement.productId ?? '',
    offeringId: offeringId ? offeringId : entitlement.offeringId || '',
  };
  return [
    {
      ...actualEntitlementDetail,
    },
    ...linkedEntitlementsData.map((linkedEntData) => ({
      entitlementId: linkedEntData.entitlementId,
      productId: linkedEntData.productId as string,
      offeringId: linkedEntData.offeringId as string,
      userTier: linkedEntData.userTier?.toString() ?? undefined,
      pricingPlanId: linkedEntData.pricingPlanId || undefined,
      chargeElement: linkedEntData.chargeElement || getChargeElement(linkedEntData.productId as string),
      isExpiredPricingPlan: linkedEntData.isExpiredPricingPlan,
      pricingPlanType: linkedEntData.pricingPlanType,
      chargeType: linkedEntData.chargeType,
      skipTrial: linkedEntData.skipTrial,
      removePromotions: linkedEntData.removePromotions,
      addPromotions: linkedEntData.addPromotions,
      promotionsToRemove: linkedEntData.promotionsToRemove,
      operations: linkedEntData.operations,
      isUpgradeTrialOrder: linkedEntData.isUpgradeTrialOrder,
      isImmediateAmendmentToFree: linkedEntData.isImmediateAmendmentToFree,
    })),
  ];
};

export const currentEntitlementType = (entitlement: EntitlementDetails): LinkedEntitlementTypes => {
  if (entitlement?.isSandbox && entitlement?.isApp) {
    return LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT;
  } else if (entitlement?.isSandbox) {
    return LinkedEntitlementTypes.SANDBOX_PRODUCT_ENTITLEMENT;
  } else if (entitlement?.isApp) {
    return LinkedEntitlementTypes.PROD_ENTITLEMENT_APP;
  } else if (entitlement?.isAddOn) {
    return LinkedEntitlementTypes.PROD_ENTITLEMENT_ADDON;
  } else if (isCollectionProductEntitlement(entitlement?.productId)) {
    return LinkedEntitlementTypes.COLLECTION_PRODUCT;
  } else if (isCollectionInstanceEntitlement(entitlement?.relatesFromEntitlements)) {
    return LinkedEntitlementTypes.COLLECTION_INSTANCE;
  }
  return LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT;
};

export const isActiveSubscription = (
  billingStatus?: string | undefined | null,
  provisioningStatus?: string | undefined | null
) => {
  if (billingStatus === BillingStatus.ACTIVE) {
    return true;
  }
  if (provisioningStatus) {
    return provisioningStatus === ProvisioningStatus.ACTIVE;
  }
  return false;
};

export const isValidURL = (string: string) => {
  try {
    return Boolean(new URL(string));
  } catch (e) {
    return false;
  }
};

export const getProductName = (item: EntitlementDetails | LinkedDestinationEntitlement) => {
  if (item?.parentSen && item?.edition) {
    return item?.edition;
  }
  return item?.productName ?? 'Unnamed product';
};

export const getChargeElement = (productId: string | undefined) => {
  if (isStatuspage(productId)) {
    return 'unit';
  }
  if (isJSMProduct(productId || '')) {
    return 'agent';
  }
  if (isJCAProduct(productId)) {
    return 'user';
  }
  return 'user';
};
